import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RichCommentBox from '../../components/RichCommentBox';
import AttachmentPreview from '../../components/AttachmentPreview';
import { taskApi, userApi, commentApi } from '../../services/api';
import Badge from '../../components/ui/Badge';
import { taskStatusVariant } from '../../utils/badgeVariants';

interface TaskDetailsPageProps {
  users?: Array<{ id:number; name:string; email:string; avatar?:string }>;
}

const TaskDetailsPage: React.FC<TaskDetailsPageProps> = ({ users: initialUsers }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const taskId = Number(id);
  const [task, setTask] = useState<any | null>(null);
  const [comments, setComments] = useState<Array<any>>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    const load = async () => {
      const data = await taskApi.getById(taskId);
      setTask(data);
    };
    load();
  }, [taskId]);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await commentApi.getAll(taskId);
      const rows = (res.data || []).slice().sort((a:any,b:any)=> new Date(b.created_at||b.createdAt).getTime() - new Date(a.created_at||a.createdAt).getTime());
      setComments(rows);
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [taskId]);

  const handleSubmit = async (payload: { task_id:number; raw_text:string; html:string; files?: File[] }) => {
    // We don't need to call the API here since RichCommentBox handles it directly
    // Just reload comments after successful submission
    await loadComments();
  };

  const handleSearchUsers = async (q: string) => {
    return await userApi.search(q);
  };

  const formatFileSize = (bytes: number): string => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  if (!task) {
    return <div className="text-sm theme-text-tertiary">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold theme-text-primary">{task.title}</h1>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="font-mono text-sm theme-text-secondary">{task.task_number || task.taskNumber}</span>
            <Badge variant={taskStatusVariant(task.status)}>{task.status}</Badge>
          </div>
        </div>
        <button onClick={()=>navigate('/tasks')} className="text-sm text-blue-600 hover:underline">Back to all tasks</button>
      </div>

      <div className="glass rounded-2xl p-3 md:p-4">
        <h2 className="text-base font-semibold theme-text-primary mb-2">Description</h2>
        <p className="text-sm theme-text-primary leading-relaxed">{task.description}</p>
      </div>

      <div className="glass rounded-2xl p-3 md:p-4">
        <h2 className="text-base font-semibold theme-text-primary mb-2">Comments & Mentions</h2>
        <RichCommentBox 
          taskId={taskId} 
          onCommentCreated={loadComments}
        />

        <div className="mt-3 space-y-2">
          {loadingComments && <div className="text-sm theme-text-tertiary">Loading comments…</div>}
          {comments.map((c:any)=> (
            <div key={c.id} className="rounded-xl theme-border theme-card p-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {(c.author?.name || 'U').split(' ').map((w:string)=>w[0]).slice(0,2).join('')}
                </div>
                <div>
                  <p className="text-sm font-medium theme-text-primary">{c.author?.name || 'Unknown'}</p>
                  <p className="text-sm theme-text-secondary">{new Date(c.created_at || c.createdAt).toLocaleString()}</p>
                </div>
              </div>
              <div className="prose prose-sm max-w-none theme-text-primary text-sm" dangerouslySetInnerHTML={{ __html: c.html || c.content }} />

              {/* Display attachments */}
              {c.attachments && c.attachments.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-sm font-medium theme-text-primary">Attachments:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {c.attachments.map((attachment: any) => (
                      <AttachmentPreview
                        key={attachment.id}
                        attachment={{
                          ...attachment,
                          url: `http://localhost:8000/storage/${attachment.file_path}`,
                          formatted_size: formatFileSize(attachment.file_size),
                          isImage: attachment.mime_type.startsWith('image/'),
                          isPdf: attachment.mime_type === 'application/pdf',
                          isDocument: attachment.mime_type.startsWith('application/') ||
                                     attachment.mime_type.startsWith('text/'),
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {!loadingComments && comments.length === 0 && (
            <div className="text-sm text-gray-500">No comments yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsPage;
